import PageLoaderLegacy from '@/app/common/components/__PageLoaderLegacy.tsx'
import Badge, { ColorType } from '@/common/components/Badge.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import {
  MessageNotificationIcon,
  UserSquareIcon,
} from '@/common/icons/general.tsx'
import { formatPhoneNumber } from '@/common/utilities.ts'
import { cn } from '@/lib/utils.ts'
import {
  FamilyApplicationDetails,
  FlagStatus,
} from '@/queries/family_applications/requests.ts'
import { Message, MessagesSchema } from '@/schemas/message.schema.ts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { format, parseISO } from 'date-fns'
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { AutosizeTextarea } from './AutosizeTextarea.tsx'

const DATE_FNS_BASE_FORMAT = 'yyyy-MM-dd hh:mmaa'

interface ChatProps {
  application: FamilyApplicationDetails
  userId: number
}

type Payload = {
  flag_id: number
  message: string
}

export function Chat({ application, userId }: ChatProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLFormElement>(null)
  const [defaultHeight, setDefaultHeight] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [selectedFlagId, setSelectedFlagId] = useState(application.flags[0]?.id)
  const queryClient = useQueryClient()
  const [newMessage, setNewMessage] = useState('')

  const { data: messages, status: messagesStatus } = useQuery({
    queryFn: async () => {
      const response = await axios.get(`/flags/${selectedFlagId}/messages`)
      return MessagesSchema.parse(response.data.data)
    },
    queryKey: ['chat', selectedFlagId],
  })

  const { mutate: sendMessage } = useMutation({
    mutationFn: async (message: Payload) => {
      const response = await axios.post(`/flags/messages`, message)
      return response.data
    },
    onMutate: async (newMessage) => {
      await queryClient.cancelQueries({
        queryKey: ['chat', selectedFlagId],
      })

      const previousMessages = queryClient.getQueryData([
        'chat',
        selectedFlagId,
      ])

      const optimisticMessage: Message = {
        id: userId,
        message: newMessage.message,
        created_datetime: new Date().toISOString(),
        user_id: userId,
      }

      queryClient.setQueryData(['chat', selectedFlagId], (old: Message[]) =>
        old ? [...old, optimisticMessage] : [optimisticMessage]
      )

      return { previousMessages }
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(
        ['chat', selectedFlagId],
        context?.previousMessages
      )
    },
  })

  function formatDateSent(timestamp: string) {
    const utcString = timestamp.replace(' ', 'T') + 'Z' // Convert to "2025-01-02T21:30:39Z"
    const newDate = parseISO(utcString)

    const response = format(newDate, DATE_FNS_BASE_FORMAT)

    return response
  }

  const formatMessageText = (text: string) => {
    const cleanedText = text.replace(/\n{3,}/g, '\n\n')

    return cleanedText.split('\n').map((paragraph, index) => (
      <p key={index} className={index > 0 ? 'mt-2' : ''}>
        {paragraph.replace(/ /g, '\u00A0')}
      </p>
    ))
  }

  useEffect(() => {
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDefaultHeight(entry.contentRect.height)
      }
    })

    resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView()
  }, [messages])

  const handleSendMessage = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (newMessage.trim() === '') return

    const message = {
      flag_id: selectedFlagId,
      message: newMessage,
    }

    sendMessage(message)
    setNewMessage('')
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  function getFlagColor(flagStatus: FlagStatus): ColorType {
    const flagTable: Record<FlagStatus, ColorType> = {
      Approved: 'Positive',
      Pending: 'Neutral',
      Rejected: 'Negative',
    }

    return flagTable[flagStatus]
  }

  const selectedFlag = application.flags.find(
    (flag) => flag.id === selectedFlagId
  )

  function isSentByCurrentUser(message: Message) {
    return message.user_id === userId
  }

  const headerHeight = headerRef.current?.clientHeight || 0
  const textareaHeight = textareaRef.current?.clientHeight || 0

  const CHAT_AREA_HEIGHT = defaultHeight - headerHeight - textareaHeight - 24

  return (
    <div
      ref={containerRef}
      className="flex-1  w-full mx-auto gap-4 flex flex-col lg:flex-row"
    >
      <Card className="basis-3/12">
        <CardHeader>
          <div className="flex items-center">
            <div className="h-10 w-10 bg-gray-100 border border-gray-200 rounded-full inline-flex items-center justify-center">
              <UserSquareIcon className="text-gray-600" />
            </div>
            <div className="flex flex-col ml-2">
              <div className="font-medium capitalize text-sm  mb-1 text-gray-800 leading-[100%]">
                {application.name}
              </div>
              <div className="text-gray-600 text-xs font-normal leading-[100%]">
                <div className="mb-1">{application.email}</div>
                {application.phone && (
                  <div>{formatPhoneNumber(application.phone)}</div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        {application.flags.length > 0 && (
          <div className=" gap-2 p-4">
            {application.flags.map((flag) => (
              <Button
                variant={'ghost'}
                key={flag.id}
                className={cn(
                  `flex border border-transparent  shadow-none py-3 justify-between items-center text-left w-full rounded-0 text-sm`,
                  {
                    'bg-gray-50 border border-gray-200':
                      selectedFlagId === flag.id,
                  }
                )}
                onClick={() => setSelectedFlagId(flag.id)}
              >
                <span className="block font-medium text-gray-800">
                  {flag.flag_name}
                </span>
                <div className="font-normal text-gray-700">
                  <Badge semanticColor={getFlagColor(flag.status)}>
                    {flag.status}
                  </Badge>
                </div>
              </Button>
            ))}
          </div>
        )}
      </Card>
      <Card className="flex-1 flex flex-col p-0 mx-0 px-0 basis-9/12">
        <div ref={headerRef}>
          <CardHeader className="flex justify-between">
            {selectedFlag && (
              <>
                {selectedFlag?.flag_name}
                <Badge semanticColor={getFlagColor(selectedFlag?.status)}>
                  {selectedFlag?.status}
                </Badge>
              </>
            )}
          </CardHeader>
        </div>
        <PageLoaderLegacy status={messagesStatus}>
          <CardContent className="flex-1 flex flex-col h-full p-0 pt-1 pb-1">
            <div
              style={{
                maxHeight: CHAT_AREA_HEIGHT,
              }}
              className="flex-1 overflow-y-auto max-h-auto space-y-4 px-4"
            >
              {messages?.map((message) => (
                <div
                  key={`${message.message}-${message.id}`}
                  className={`flex ${
                    isSentByCurrentUser(message)
                      ? 'justify-end'
                      : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-lg text-sm whitespace-pre-wrap break-words rounded-lg p-3 ${
                      isSentByCurrentUser(message)
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 border border-gray-200 text-gray-800'
                    }`}
                  >
                    <div className="message-content">
                      {formatMessageText(message.message)}
                    </div>
                    <span className="text-xs opacity-75 block mt-2 pt-1 border-current">
                      {formatDateSent(message.created_datetime)}
                    </span>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </CardContent>

          <form
            ref={textareaRef}
            onSubmit={handleSendMessage}
            className="flex gap-2 px-2 py-2"
          >
            <AutosizeTextarea
              maxHeight={200}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Message"
              className="border-none font-normal text-gray-800 rounded-none"
            />
            <Button className="rounded-full h-10 w-10" type="submit">
              <MessageNotificationIcon />
            </Button>
          </form>
        </PageLoaderLegacy>
      </Card>
    </div>
  )
}
